/**
 * WeCo API Client — Base HTTP client with authentication
 * 
 * Provides a wrapper around fetch with:
 * - Automatic Bearer token injection
 * - Token refresh on 401
 * - Error handling and typing
 */

import {
  THROTTLE_STATUS,
  THROTTLED_MESSAGE,
  isWorkspacePausedBody,
  reportWorkspacePaused,
  throttleFieldsFrom,
} from "@weco/suite-client";
import { useShellStore } from "../store";
import {
  API_AUTH_PREFIX,
  API_CALENDAR_PREFIX,
  API_DOCS_PREFIX,
  API_DRIVE_PREFIX,
  API_EMAIL_PREFIX,
  API_VIDEO_PREFIX,
} from "../config/apiRoutes";

export interface ApiError {
  message: string;
  code: number;
  details?: unknown;
}

export class ApiClientError extends Error {
  code: number;
  details?: unknown;
  /** Seconds from the `Retry-After` header (a 429 usually carries one). */
  retryAfter?: number;
  /** `Date.now()` when the failed response arrived. */
  receivedAt: number;

  constructor(
    message: string,
    code: number,
    details?: unknown,
    { retryAfter, receivedAt }: { retryAfter?: number; receivedAt?: number } = {},
  ) {
    super(message);
    this.name = "ApiClientError";
    this.code = code;
    this.details = details;
    this.retryAfter = retryAfter;
    this.receivedAt = receivedAt ?? Date.now();
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * API client with automatic authentication and token refresh
 */
export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await useShellStore.getState().getValidAccessToken();
    
    if (!token) {
      throw new ApiClientError("No access token available", 401);
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const throttle = throttleFieldsFrom(response);
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorDetails: unknown;

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.detail || errorMessage;
        errorDetails = errorData;
        // Paused workspace: the shell handles it once (see lib/billing).
        if (response.status === 403 && isWorkspacePausedBody(errorData)) {
          reportWorkspacePaused(typeof errorData.detail === "string" ? errorData.detail : undefined);
        }
      } catch {
        // Response may not be JSON
      }

      // Never surface DRF's raw "Request was throttled…" text (#1747 D1).
      if (response.status === THROTTLE_STATUS) errorMessage = THROTTLED_MESSAGE;
      throw new ApiClientError(errorMessage, response.status, errorDetails, throttle);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const headers = skipAuth ? {} : await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...headers,
        ...init.headers,
      },
      ...init,
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const headers = skipAuth ? {} : await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...headers,
        ...init.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      ...init,
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const headers = skipAuth ? {} : await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...headers,
        ...init.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      ...init,
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const headers = skipAuth ? {} : await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...headers,
        ...init.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      ...init,
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const headers = skipAuth ? {} : await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...headers,
        ...init.headers,
      },
      ...init,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Upload file with multipart/form-data
   */
  async upload<T>(path: string, formData: FormData, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const headers = skipAuth ? {} : await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: {
        ...headers,
        ...init.headers,
      },
      body: formData,
      ...init,
    });

    return this.handleResponse<T>(response);
  }
}

/**
 * Service-specific API clients
 */
/** People identity API (same-origin `/api/auth` → People `/api/v1.0/auth/` in dev). */
export const peopleApi = new ApiClient(API_AUTH_PREFIX);

/**
 * People core resource API (`/api/people/api/v1.0/...` → Django `/api/v1.0/...`).
 * Use for `/users/me/`, preferences, etc. (not JWT auth-only routes under `/api/auth/`).
 */
export const peopleCoreApi = new ApiClient("/api/people/api/v1.0");

export const emailApi = new ApiClient(API_EMAIL_PREFIX);
export const driveApi = new ApiClient(API_DRIVE_PREFIX);
export const docsApi = new ApiClient(API_DOCS_PREFIX);
export const videoApi = new ApiClient(API_VIDEO_PREFIX);
export const calendarApi = new ApiClient(API_CALENDAR_PREFIX);
